import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Chip from '@material-ui/core/Chip';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import Button from 'components/StyledButton/StyledButton';
import Header from 'pages/plasticAssessment/components/Header/Header';
import { ReduxState } from 'redux/types';
import Company from 'types/Company';
import snackbar from 'utils/snackbar';

import StyledActionButton from '../../../../common/styled/StyledActionButton/StyledActionButton';
import StyledTableContainer from '../../../../common/styled/StyledTableContainer/StyledTableContainer';
import CustomLoader from '../../../../components/CustomLoader/CustomLoader';
import PlasticReductionPlanItemDetailsDialog from '../../../../dialogs/PlasticReductionPlanItemDetailsDialog/PlasticReductionPlanItemDetailsDialog';
import PlasticReductionPlan from '../../../../types/PlasticReductionPlan';
import api from '../../../../utils/api';
import { downloadFile } from '../../../../utils/download';
import { ActionTableCell, FlexContainer } from '../../styled';

interface Props {
    companyData: Company;
    assessmentYear: number | null;
    companyId: number;
    assessmentId: number;
    tabsRender: JSX.Element;
    isCustomer: boolean;
    fullWidthSelector: boolean;
    exportWholePdf: () => void;
    setIsCompanyDialogOpen: Dispatch<SetStateAction<boolean>>;
}

const ReductionPlanTab = (props: Props) => {
    const { companyData, assessmentYear, companyId, assessmentId, tabsRender, isCustomer, fullWidthSelector, exportWholePdf, setIsCompanyDialogOpen } = props;

    const { t } = useTranslation();

    const userInfo = useSelector((state: ReduxState) => state.user);
    const isAdmin = userInfo.type === 'admin';

    const [isLoading, setIsLoading] = useState(true);
    const [plans, setPlans] = useState<PlasticReductionPlan[]>([]);
    const [selectedItemId, selectItemId] = useState(0);

    const [isItemDetailsDialogOpen, setIsItemDetailsDialogOpen] = useState(false);

    const getData = useCallback(() => {
        api.request('/companies/' + companyId + '/plastic-assessments/' + assessmentId + '/plans', 'GET').then((res) => {
            setPlans(res);
            setIsLoading(false);
        });
    }, [assessmentId, companyId]);

    const exportData = useCallback((format: 'csv' | 'pdf') => {
        api.request('/companies/' + companyId + '/plastic-assessments/' + assessmentId + '/plans/export/' + format, 'GET', null, true, 'blob').then(res => {
            downloadFile(res, 'export.' + format);
        });
    }, [assessmentId, companyId]);

    const closeItemDialog = useCallback((itemId?: number) => {
        if (itemId) {
            selectItemId(itemId);
        }
        setIsItemDetailsDialogOpen(false);
    }, []);

    useEffect(() => {
        if (!isItemDetailsDialogOpen) getData();
    }, [getData, isItemDetailsDialogOpen]);

    const createDeleteGp = useCallback((planId: number, create: boolean) => {
        api.request('/companies/' + companyId + '/plastic-assessments/' + assessmentId + '/plans/' + planId + '/gp', create ? 'POST' : 'DELETE').then((res) => {
            getData();
            if (create) {
                snackbar.success(t('dialogs.good_practice_details.new_good_practice_saved'));
            } else {
                snackbar.success(t('dialogs.good_practice_delete.success'));
            }
        });
    }, [t, getData, companyId, assessmentId]);

    if (isLoading) {
        return (
            <>
                <Header companyName={companyData.name} assessmentYear={assessmentYear} setIsCompanyDialogOpen={setIsCompanyDialogOpen} />
                <FlexContainer>
                    {tabsRender}
                </FlexContainer>
                <CustomLoader />
            </>
        );
    }

    return (
        <>
            <Header companyName={companyData.name} assessmentYear={assessmentYear} setIsCompanyDialogOpen={setIsCompanyDialogOpen} />
            {fullWidthSelector && (
                <div style={{ marginBottom: '14px' }}>
                    {tabsRender}
                </div>
            )}
            <FlexContainer>
                {!fullWidthSelector && <>{tabsRender}</>}
                <div>
                    <StyledActionButton color='primary' variant='outlined' onClick={() => exportData('csv')} >
                        {t('pages.plastic_assessment.export_csv')}
                    </StyledActionButton>{' '}
                    <StyledActionButton color='primary' variant='outlined' onClick={() => exportData('pdf')} >
                        {t('pages.plastic_assessment.export_pdf')}
                    </StyledActionButton>{' '}
                    <StyledActionButton color='primary' variant='outlined' onClick={() => exportWholePdf()} >
                        {t('pages.plastic_assessment.export_whole_pdf')}
                    </StyledActionButton>
                </div>
            </FlexContainer>

            <Alert style={{ maxWidth: '1200px', margin: '12px auto 0' }} severity='info'>
                <Trans i18nKey='pages.plastic_assessment.tabs.plastic_reduction_plan.help'>
                    _<AlertTitle>_</AlertTitle>_
                    <b>_</b>
                </Trans>
            </Alert>

            <StyledTableContainer component={Paper} style={{ marginTop: '20px' }}>
                <Table aria-label='simple table'>
                    <TableHead>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>{t('pages.plastic_assessment.tabs.plastic_reduction_plan.table.item')}</TableCell>
                            <TableCell>{t('pages.plastic_assessment.tabs.plastic_reduction_plan.table.description')}</TableCell>
                            <TableCell>{t('pages.plastic_assessment.tabs.plastic_reduction_plan.table.type')}</TableCell>
                            <TableCell>{t('pages.plastic_assessment.tabs.plastic_reduction_plan.table.target')}</TableCell>
                            {/*<TableCell>{t('pages.plastic_assessment.tabs.plastic_reduction_plan.table.action')}</TableCell>*/}
                            <TableCell>{t('pages.plastic_assessment.tabs.plastic_reduction_plan.table.deadline')}</TableCell>
                            <TableCell>{t('pages.plastic_assessment.tabs.plastic_reduction_plan.table.certification_year')}</TableCell>
                            {/*<TableCell>{t('pages.plastic_assessment.tabs.plastic_reduction_plan.table.done')}</TableCell>*/}
                            {/*<TableCell>{t('pages.plastic_assessment.tabs.plastic_reduction_plan.table.responsible')}</TableCell>*/}
                            <TableCell>{t('pages.plastic_assessment.tabs.plastic_reduction_plan.table.target_reached')}</TableCell>
                            {isAdmin && (
                                <ActionTableCell>{t('pages.companies.table.actions')}</ActionTableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {plans.map((plan, index) => {
                            const statusMap = {
                                0: {
                                    color: '#fdecea',
                                    borderColor: '#f44336',
                                    label: 'No',
                                    outlined: false
                                },
                                1: {
                                    color: '#edf7ed',
                                    borderColor: '#4caf50',
                                    label: 'Sì',
                                    outlined: false
                                }
                            };

                            const statusData = statusMap[plan.targetReached ? 1 : 0];

                            return (
                                <TableRow key={plan.id}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{plan.item?.description}</TableCell>
                                    <TableCell>{plan.actionDescription}</TableCell>
                                    <TableCell>{plan.actionTitle}</TableCell>
                                    <TableCell>{plan.item?.reductionTarget}%</TableCell>
                                    <TableCell>{plan.item?.dueDate ?? plan.actionDeadline}</TableCell>
                                    <TableCell>{plan.certificationYear}</TableCell>
                                    {/*
                                    <TableCell>
                                        {plan.actionDone ? t('pages.generic.table.flag_yes') : t('pages.generic.table.flag_no')}
                                    </TableCell>
                                    */}
                                    {/*<TableCell>{plan.actionResponsible}</TableCell>*/}
                                    <TableCell style={{ padding: '10px 16px' }}>
                                        <Chip label={statusData.label} variant={statusData.outlined ? 'outlined' : 'default'} style={{ ...(statusData.outlined ? {} : { backgroundColor: statusData.color, border: '1px solid ' + statusData.borderColor }), width: '90px' }} />
                                        {plan.targetReached}
                                    </TableCell>
                                    {isAdmin && (
                                        <ActionTableCell>
                                            {plan.goodPracticeId ? (
                                                <Button variant='outlined' color='primary' onClick={() => createDeleteGp(plan.id, false)}>
                                                    {t('pages.plastic_assessment.tabs.plastic_reduction_plan.table.delete_gp_button')}
                                                </Button>
                                            ) : (
                                                <Button variant='outlined' color='primary' onClick={() => createDeleteGp(plan.id, true)}>
                                                    {t('pages.plastic_assessment.tabs.plastic_reduction_plan.table.create_gp_button')}
                                                </Button>
                                            )}
                                        </ActionTableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </StyledTableContainer>

            <PlasticReductionPlanItemDetailsDialog open={isItemDetailsDialogOpen} onClose={closeItemDialog} companyId={companyId} assessmentId={assessmentId} planId={selectedItemId} isCustomer={isCustomer} />
        </>
    );
};

export default ReductionPlanTab;
